// src/controlsil/CrearReporteSIL.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
} from "@mui/material";

export const CrearReporteSIL = ({ onCreated }) => {
  const toast = useToast();

  // =========================
  // Estado general
  // =========================
  const [isSubmitting, setIsSubmitting] = useState(false);

  // =========================
  // Nodo asignado
  // =========================
  const [miNodo, setMiNodo] = useState(null);
  const [loadingNodo, setLoadingNodo] = useState(true);

  // =========================
  // Ausencia
  // =========================
  const [ausente, setAusente] = useState(false);
  const [loadingAusencia, setLoadingAusencia] = useState(false);
  const [ausenciaDetalle, setAusenciaDetalle] = useState(null);

  // =========================
  // Formulario
  // =========================
  const [estado, setEstado] = useState("verde");
  const [comentario, setComentario] = useState("");

  // =========================
  // Helpers auth
  // =========================
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const getMyUserId = () => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.id) return Number(u.id);
      }
    } catch {}
    const rawId = localStorage.getItem("user_id");
    return rawId ? Number(rawId) : null;
  };

  // =========================
  // Cargar nodo asignado
  // =========================
  useEffect(() => {
    (async () => {
      setLoadingNodo(true);
      try {
        const myUserId = getMyUserId();
        if (!myUserId) {
          setMiNodo(null);
          toast.error("No se pudo identificar el usuario actual.");
          return;
        }

        const res = await fetch(`${Global.url}nodos/organigrama`, {
          headers: authHeaders(),
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) {
          setMiNodo(null);
          toast.error("No se pudo cargar el nodo asignado.");
          return;
        }

        const nodo =
          (Array.isArray(data) ? data : []).find(
            (n) => Number(n.usuario_id) === Number(myUserId)
          ) || null;

        setMiNodo(nodo);

        if (!nodo) {
          toast.warning("No tienes un nodo asignado. Contacta con un administrador.");
        }
      } catch (e) {
        console.error(e);
        setMiNodo(null);
        toast.error("Error de red al obtener el nodo asignado.");
      } finally {
        setLoadingNodo(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // Cargar ausencia
  // =========================
  useEffect(() => {
    if (!miNodo?.id) return;

    (async () => {
      setLoadingAusencia(true);
      try {
        const res = await fetch(`${Global.url}nodos/ausencias/activas`, {
          headers: authHeaders(),
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) {
          setAusente(false);
          setAusenciaDetalle(null);
          return;
        }

        const a =
          (Array.isArray(data) ? data : []).find(
            (x) => Number(x.nodo_id) === Number(miNodo.id)
          ) || null;

        setAusente(!!a);
        setAusenciaDetalle(a);
      } catch (e) {
        console.error(e);
        setAusente(false);
        setAusenciaDetalle(null);
      } finally {
        setLoadingAusencia(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miNodo?.id]);

  // =========================
  // Derivados
  // =========================
  const estadoNoVerde = estado !== "verde";

  const puedeEnviar = useMemo(() => {
    if (!miNodo) return false;
    if (ausente) return false;
    if (!estado) return false;
    if (estadoNoVerde && !comentario.trim()) return false;
    return true;
  }, [miNodo, ausente, estado, comentario, estadoNoVerde]);

  // =========================
  // Acciones
  // =========================
  const toggleAusencia = async () => {
    if (!miNodo?.id || loadingAusencia) return;

    setLoadingAusencia(true);
    try {
      const url = ausente
        ? `${Global.url}nodos/${miNodo.id}/ausencia/off`
        : `${Global.url}nodos/${miNodo.id}/ausencia/on`;

      const body = ausente
        ? undefined
        : JSON.stringify({ motivo: "Ausente" });

      const res = await fetch(url, {
        method: "POST",
        headers: {
          ...(body ? { "Content-Type": "application/json" } : {}),
          ...authHeaders(),
        },
        body,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "No se pudo actualizar la ausencia.");
        return;
      }

      if (ausente) {
        setAusente(false);
        setAusenciaDetalle(null);
        toast.success("Ausencia desactivada.");
      } else {
        setAusente(true);
        setAusenciaDetalle(data?.data || null);
        setEstado("verde");
        setComentario("");
        toast.success("Marcado como ausente.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de red actualizando la ausencia.");
    } finally {
      setLoadingAusencia(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!puedeEnviar || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(`${Global.url}reportes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({
          estado,
          comentario: comentario.trim() || null,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "Error al crear el reporte.");
        return;
      }

      toast.success("Reporte registrado correctamente.");
      setEstado("verde");
      setComentario("");

      if (typeof onCreated === "function") {
        onCreated(data?.data || data);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al crear el reporte.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        {/* Header */}
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Nuevo reporte SIL
          </Typography>
        </Box>

        {/* Formulario */}
        <Box className="contentContainer contentContainer--formSingle">
          <Paper className="formPaper">
            <Stack component="form" spacing={2} onSubmit={handleSubmit}>
              {/* Nodo */}
              <Box>
                <Typography sx={{ fontWeight: 700 }}>Nodo asignado</Typography>

                {loadingNodo ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">Cargando…</Typography>
                  </Stack>
                ) : miNodo ? (
                  <Typography variant="body2" color="text.secondary">
                    {miNodo.titulo}
                    {miNodo.etiqueta ? ` · ${miNodo.etiqueta}` : ""}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="error">
                    Sin nodo asignado
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Ausencia */}
              <FormControlLabel
                control={
                  <Switch
                    checked={ausente}
                    onChange={toggleAusencia}
                    disabled={loadingAusencia || !miNodo}
                  />
                }
                label={ausente ? "Ausente" : "Presente"}
              />

              {/* Estado */}
              <FormControl fullWidth disabled={!miNodo || ausente}>
                <InputLabel id="estado-label">Estado</InputLabel>
                <Select
                  labelId="estado-label"
                  label="Estado"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                >
                  <MenuItem value="verde">🟢 Verde (OK)</MenuItem>
                  <MenuItem value="amarillo">🟡 Amarillo (Atención)</MenuItem>
                  <MenuItem value="rojo">🔴 Rojo (Problema)</MenuItem>
                </Select>
              </FormControl>

              {/* Comentario */}
              <TextField
                label={`Comentario ${estadoNoVerde ? "(obligatorio)" : "(opcional)"}`}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                multiline
                minRows={3}
                disabled={!miNodo || ausente}
                required={estadoNoVerde}
              />

              {/* Acciones */}
              <Box className="formActions">
                <Button type="submit" disabled={!puedeEnviar || isSubmitting}>
                  {isSubmitting ? "Enviando..." : "Registrar reporte"}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export default CrearReporteSIL;
